import { BreakpointObserver } from '@angular/cdk/layout';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  Subject,
  take,
  tap
} from 'rxjs';

import {
  ChatMessage,
  ShortenedMessage
} from '../../features/chatbot/model/chat-message.model';
import { ChatbotService } from '../../features/chatbot/data-access/chatbot.service';

import {
  ChatQuerySettings,
  ModelType,
  SearchMode,
  ChatMode,
  Source
} from '../../shared/model/query-body.model';

import {
  CHAT_HISTORY,
  Configuration,
  CONFIGURATION_NAME,
} from '../model/configurations.model';
import { SK_GPT } from '../model/configurations/skGPT';
import { LoaderService } from './loader.service';
import { NavigationStart, Router } from '@angular/router';
import { MenuRootRoute } from 'src/app/app.routes';

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private chatbotService = inject(ChatbotService);
  private snackBar = inject(MatSnackBar);
  private breakpointObserver = inject(BreakpointObserver);
  private translateService = inject(TranslateService);
  private loader = inject(LoaderService);
  private router = inject(Router);

  /* Properties: StateService */
  readonly chatHistory$ = new BehaviorSubject<ChatMessage[]>([]);

  /* Properties: whole app */
  currentLang$ = new BehaviorSubject(this.translateService.currentLang);
  readonly selectedConfiguration$: BehaviorSubject<Configuration>;
  readonly configurations$ = new BehaviorSubject<Configuration[]>([
    SK_GPT,
  ]);
  readonly isLoading$ = new BehaviorSubject<boolean>(false);
  readonly isSideNavOpen$ = new BehaviorSubject<boolean>(true);
  readonly isTabletLandscapeUp$ = this.breakpointObserver
    .observe('(min-width: 900px)')
    .pipe(
      map(state => state.matches),
      tap(isTabletLandscapeUp => {
        this.isSideNavOpen$.next(isTabletLandscapeUp);
      })
    );

  readonly isDarkMode$ = new BehaviorSubject<boolean>(false);

  /* Properties: settings */
  readonly selectedModel$ = new BehaviorSubject<ModelType>(ModelType.GPT_4_1);
  readonly searchMode$ = new BehaviorSubject<SearchMode>(SearchMode.HYBRID);
  readonly chatMode$ = new BehaviorSubject<ChatMode>(ChatMode.OPENAI);
  readonly topKValue$ = new BehaviorSubject<number>(6);
  readonly selectedSource$ = new BehaviorSubject<Source>(Source.ALL);

  readonly isShowExpertMode$ = new BehaviorSubject<boolean>(false);
  readonly isExpertMode$ = new BehaviorSubject<boolean>(false);
  readonly expertModeTemperature$ = new BehaviorSubject<number>(0.1);
  readonly isFullPageMode$ = new BehaviorSubject<boolean>(false);

  /* Properties: Intelligent Search - Q&A, Document chatting */
  readonly example$ = new Subject<string>();

  /* Properties: Intelligent Search - Chatting*/
  readonly exampleQuestion$ = new BehaviorSubject<string>('');

  /* Properties: Intelligent Search - Chatting */
  readonly chat$ = this.chatbotService.chat$;
  protected taskIds$ = new BehaviorSubject<string[]>([]);

  /* Properties: Document chatting, proposal generator */
  protected uploadedFiles$ = new BehaviorSubject<File[]>([]);

  constructor() {
    this.selectedConfiguration$ = new BehaviorSubject<Configuration>(
      this.loadConfiguration()
    );
    this.isDarkMode$.next(this.loadIsDarkMode());
    this.subscribeToConfiguration();
    this.subscribeToChatUpdates();
    this.subscribeToURLChanges();
    this.subscribeToLangChanges();
    this.subscribeToDarkModeChanges();
  }

  private subscribeToConfiguration(): void {
    this.selectedConfiguration$
      .pipe(takeUntilDestroyed())
      .subscribe(configuration => {
        localStorage.setItem(CONFIGURATION_NAME, configuration.shortName);
        this.uploadedFiles$.next([]);
        this.loader.isLoading$.next(false);
      });
  }

  private subscribeToChatUpdates() {
    this.chatbotService.chatUpdated$
      .pipe(takeUntilDestroyed())
      .subscribe((res: ChatMessage[]) => {
          this.chatHistory$.next(res);
          const currentConfigName =
            this.selectedConfiguration$.value.shortName.replace(/\s+/g, '_');
          localStorage.setItem(
            `${CHAT_HISTORY}_${currentConfigName}`,
            JSON.stringify(this.chatHistory$.value)
          );
      });
  }

  private subscribeToURLChanges() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart),
        takeUntilDestroyed()
      )
      .subscribe(navigation => {
        const { url } = navigation as NavigationStart;

        this.updatePageSpecificFlags(url);

        this.uploadedFiles$.next([]);
        this.loader.isLoading$.next(false);
      });
  }

  private subscribeToDarkModeChanges() {
    this.isDarkMode$.pipe(takeUntilDestroyed()).subscribe(isDarkMode => {
      localStorage.setItem('isDarkMode', String(isDarkMode));
    });
  }

  private updatePageSpecificFlags(url: string) {
    const isChatUrl =
      url === `/${MenuRootRoute.CHAT}`;

    // this.isShowExpertMode$.next(
    //     isChatUrl
    // );
  }

  private subscribeToLangChanges(): void {
    this.translateService.onLangChange.subscribe(lang => {
      this.updateExampleQuestion();
      this.currentLang$.next(lang.lang);
    });
  }

  private updateExampleQuestion() {
    const showExampleQuestion =
      this.chatbotService.chatHistorySubject.getValue().length === 1;
    if (showExampleQuestion) {
      this.exampleQuestion$.next(
        this.translateService.instant('example.exampleQuestion')
      );
    }
  }

  updateSelectedConfiguration(configuration: Configuration): void {
    this.selectedConfiguration$.next(configuration);
    this.loadChatHistory();

    this.uploadedFiles$.next([]);
    this.isLoading$.next(false);
  }

  useExample(example: string): void {
    this.exampleQuestion$.next(example);
  }

  async chat(
    message: string,
    filterByFiles?: string[],
    filterByTaskId?: string[]
  ): Promise<void> {
    const chatHistoryShortened: ShortenedMessage[] =
      this.chatHistory$.value.map(chatMessage => ({
        user: chatMessage?.userMessage?.user ?? undefined,
        bot: chatMessage?.botMessage?.bot?.answerText ?? undefined
      }));
    this.example$.next('');

    const chatQuerySettings: ChatQuerySettings =
      this.getChatQuerySettings();

    this.chatbotService
      // reverse the chat history before sending it to the backend:
      .handleChat(
        message,
        [...chatHistoryShortened], //.reverse(),
        chatQuerySettings,
        filterByFiles,
        filterByTaskId
      );
  }

  toggleSideNav(isOpen?: boolean): void {
    if (isOpen !== undefined) {
      this.isSideNavOpen$.next(isOpen);
      return;
    }
    this.isSideNavOpen$.next(!this.isSideNavOpen$.value);
  }

  getChatWelcomeMessage(): ChatMessage {
    return {
      userMessage: {
        user: '',
        userTimestamp: null
      },
      botMessage: {
        bot: {
          answerText: this.translateService.instant('example.welcomeMessage'),
          error_code: null,
          index: '',
          prompt: null,
          runtimeInMillis: undefined,
          sources: []
        },
        botTimestamp: new Date().toLocaleString()
      }
    };
  }

  loadChatHistory() {
    const configName = this.selectedConfiguration$.value.shortName.replace(
      /\s+/g,
      '_'
    );

    const savedChatHistory = JSON.parse(
      localStorage.getItem(`${CHAT_HISTORY}_${configName}`) ?? '[]'
    ) as ChatMessage[];

    if (savedChatHistory.length === 0) {
      savedChatHistory.push(this.getChatWelcomeMessage());
    }
    this.chatbotService.updateChatHistory(savedChatHistory);
    this.chatHistory$.next(savedChatHistory);
  }

  setEmptyChatHistory() {
    this.exampleQuestion$.next(
      this.translateService.instant('example.exampleQuestion')
    );
    this.chatbotService.updateChatHistory([this.getChatWelcomeMessage()]);
    this.chatHistory$.next([this.getChatWelcomeMessage()]);
  }

  clearChatHistory() {
    this.chatbotService
      .deleteHistory()
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.snackBar.open(
            response,
            this.translateService.instant('buttons.close'),
            {
              panelClass: ['mat-snackbar-success'],
              duration: 3000
            }
          );
          const configName =
            this.selectedConfiguration$.value.shortName.replace(/\s+/g, '_');
          localStorage.removeItem(`${CHAT_HISTORY}_${configName}`);
          this.chatHistory$.next([]);
          this.chatbotService.updateChatHistory([]);
          this.loadChatHistory();
        },
        error: (error: unknown) => this.errorHandler(error)
      });
  }

  getTaskIdsStream(): Observable<string[]> {
    return this.taskIds$;
  }

  updateSelectedModel(model: ModelType) {
    this.selectedModel$.next(model);
    const config = this.selectedConfiguration$.value;
    config.searchSettings.retriever_number = model === ModelType.GPT_4_1 ? 7 : 6;
    this.selectedConfiguration$.next(config);
  }

  updateSearchMode(mode: SearchMode) {
    this.searchMode$.next(mode);
  }

  updateChatMode(mode: ChatMode) {
    this.chatMode$.next(mode);
  }

  updateTopKValue(value: number): void {
    this.topKValue$.next(value);
  }

  updateSelectedSource(source: Source): void {
    this.selectedSource$.next(source);
  }

  updateIsExpertMode(isExpertMode: boolean): void {
    this.isExpertMode$.next(isExpertMode);
  }

  updateExpertModeTemperature(temperature: number): void {
    this.expertModeTemperature$.next(temperature);
  }

  private errorHandler(error: any): void {
    this.isLoading$.next(false);
    this.loader.isLoading$.next(false);
    this.snackBar.open(
      `Request failed with status code ${error.status}`,
      this.translateService.instant('buttons.close'),
      {
        panelClass: ['mat-snackbar-warn'],
        duration: 3000
      }
    );
  }

  private loadConfiguration(): Configuration {
    const configurationName = localStorage.getItem(CONFIGURATION_NAME);
    return (
      this.configurations$.value.find(
        configuration => configuration.shortName === configurationName
      ) || SK_GPT
    );
  }

  private loadIsDarkMode(): boolean {
    const item = localStorage.getItem('isDarkMode');
    if (item) {
      return JSON.parse(item) === true;
    }
    return false;
  }

  private getChatQuerySettings(): ChatQuerySettings {
    return {
      similarity_top_k: this.topKValue$.value,
      model: this.selectedModel$.value,
      search_mode: this.searchMode$.value,
      chat_mode: this.chatMode$.value,
      streaming: true,
      evaluate: false,
      fast: true,
      temperature: this.expertModeTemperature$.value,
      expert_mode: this.isExpertMode$.value,
      source: this.selectedSource$.value
    } as ChatQuerySettings;
  }
}
